import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  PAYMENT_KIND_LABELS, practicePaymentsApi, type ContactPayments, type ContractPayments, type PaymentKind,
} from '../api/practicePayments'
import { dateIt, eur, errMsg } from '../lib/financeFormat'
import { Callout, Card, CardContent, CardHeader, DataTable, DataTableEmptyRow, KeyValueList, StatusPill, type PillTone } from './ui'

const KIND_TONE: Record<PaymentKind, PillTone> = { Pagamento: 'success', Storno: 'error', Ricorrente: 'info' }

const NOT_COLLECTED = 'Riconosciuto dalla mandante: non è incasso (l’incasso vero è quello bancario).'

function monthIt(iso: string): string {
  return new Date(iso + 'T00:00:00').toLocaleDateString('it-IT', { month: 'long', year: 'numeric' })
}

/**
 * IMP-3 — sezione "Movimenti mandante" della pagina pratica: pagamenti, storni e ricorrenti importati dai report della mandante,
 * con data e importo (l'importo solo per chi ha `CanSeeRealAmounts`: altrimenti la colonna non c'e'). Se non ci sono movimenti la
 * sezione non compare. Un 403/404 la omette in silenzio, come le altre sezioni economiche della pagina.
 */
export function MandantePaymentsPanel({ contractId }: { contractId: number }) {
  const [data, setData] = useState<ContractPayments | null>(null)
  const [error, setError] = useState('')

  useEffect(() => {
    let alive = true
    setData(null)
    setError('')
    practicePaymentsApi.byContract(contractId)
      .then(r => { if (alive) setData(r) })
      .catch(e => { if (alive && !/403|404|forbidden|not found/i.test(errMsg(e, ''))) setError(errMsg(e, 'Movimenti mandante non disponibili.')) })
    return () => { alive = false }
  }, [contractId])

  if (error) return <Callout tone="error">{error}</Callout>
  if (!data || data.items.length === 0) return null
  const t = data.totals

  return (
    <Card variant="outlined" padding="none">
      <CardHeader icon="payments" title="Movimenti mandante" />
      <DataTable variant="plain" aria-label="Movimenti mandante">
        <thead>
          <tr>
            <th>Data</th><th>Tipo</th><th>Mandante</th><th>Prodotto</th>
            {data.canSeeAmounts && <th className="text-end">Importo</th>}
          </tr>
        </thead>
        <tbody>
          {data.items.map(r => (
            <tr key={r.id}>
              <td>{dateIt(r.postedOn)}<div className="text-muted md-typescale-body-small">report {monthIt(r.periodMonth)}</div></td>
              <td>
                <StatusPill tone={KIND_TONE[r.kind]}>{PAYMENT_KIND_LABELS[r.kind]}</StatusPill>
                {!r.countsForAgentCommissions && <div className="text-muted md-typescale-body-small">fuori dalle provvigioni agenti</div>}
              </td>
              <td>{r.mandanteName ?? '—'}</td>
              <td>{r.productName ?? '—'}</td>
              {data.canSeeAmounts && <td className="text-end md-num">{eur(r.amount)}</td>}
            </tr>
          ))}
        </tbody>
      </DataTable>
      <CardContent>
        {t && (
          <KeyValueList
            className="md-typescale-body-small"
            items={[
              { label: 'Pagato dalla mandante', value: <>{eur(t.paid)}</> },
              { label: 'Storni', value: <>{eur(t.storni)}</> },
              { label: 'Netto', value: <>{eur(t.net)}</> },
              { label: 'Ricorrenti (di Equal)', value: <>{eur(t.recurring)}</> },
              { label: 'Totale', value: <strong>{eur(t.total)}</strong> },
            ]}
          />
        )}
        <div className="text-muted md-typescale-body-small mt-2">{NOT_COLLECTED}</div>
      </CardContent>
    </Card>
  )
}

/**
 * IMP-3 — scheda cliente: riepilogo di TUTTE le pratiche del cliente e di quanto hanno fruttato (pagato mandante - storni,
 * ricorrenti a parte, totale). Le pratiche senza movimenti compaiono comunque, con "—". Importi solo con `CanSeeRealAmounts`.
 */
export function ContactEarningsPanel({ contactId }: { contactId: number }) {
  const [data, setData] = useState<ContactPayments | null>(null)
  const [error, setError] = useState('')

  useEffect(() => {
    let alive = true
    setData(null)
    setError('')
    practicePaymentsApi.byContact(contactId)
      .then(r => { if (alive) setData(r) })
      .catch(e => { if (alive && !/403|404|forbidden|not found/i.test(errMsg(e, ''))) setError(errMsg(e, 'Riepilogo non disponibile.')) })
    return () => { alive = false }
  }, [contactId])

  if (error) return <Callout tone="error">{error}</Callout>
  if (!data) return null
  const t = data.totals

  return (
    <Card variant="outlined" padding="none">
      <CardHeader icon="savings" title="Quanto hanno fruttato le pratiche" />
      <DataTable variant="plain" aria-label="Pratiche del cliente e quanto hanno fruttato">
        <thead>
          <tr>
            <th>Pratica</th><th>Stato</th>
            {data.canSeeAmounts && <><th className="text-end">Pagato</th><th className="text-end">Storni</th><th className="text-end">Ricorrenti</th><th className="text-end">Netto</th></>}
            {!data.canSeeAmounts && <th className="text-end">Movimenti</th>}
          </tr>
        </thead>
        <tbody>
          {data.contracts.length === 0 && <DataTableEmptyRow colSpan={data.canSeeAmounts ? 6 : 3} title="Nessuna pratica" description="Nessuna pratica visibile per questo cliente." />}
          {data.contracts.map(c => (
            <tr key={c.contractId}>
              <td>
                <Link to={`/contracts/${c.contractId}`}>{c.referenceCode}</Link>
                {c.productName && <div className="text-muted md-typescale-body-small">{c.productName}</div>}
              </td>
              <td><StatusPill tone="neutral">{c.status}</StatusPill></td>
              {data.canSeeAmounts && (
                c.movements === 0
                  ? <td colSpan={4} className="text-end text-muted">nessun movimento</td>
                  : <>
                    <td className="text-end md-num">{eur(c.paid)}</td>
                    <td className="text-end md-num">{eur(c.storni)}</td>
                    <td className="text-end md-num">{eur(c.recurring)}</td>
                    <td className="text-end md-num">{eur(c.net)}</td>
                  </>
              )}
              {!data.canSeeAmounts && <td className="text-end md-num">{c.movements}</td>}
            </tr>
          ))}
        </tbody>
      </DataTable>
      <CardContent>
        {t ? (
          <KeyValueList
            className="md-typescale-body-small"
            items={[
              { label: 'Pagato dalla mandante', value: <>{eur(t.paid)}</> },
              { label: 'Storni', value: <>{eur(t.storni)}</> },
              { label: 'Netto', value: <>{eur(t.net)}</> },
              { label: 'Ricorrenti (di Equal)', value: <>{eur(t.recurring)}</> },
              { label: 'Totale', value: <strong>{eur(t.total)}</strong> },
            ]}
          />
        ) : (
          <div className="text-muted md-typescale-body-small">Gli importi non sono visibili con il tuo profilo.</div>
        )}
        <div className="text-muted md-typescale-body-small mt-2">
          {data.contractsWithMovements} di {data.contracts.length} pratiche con movimenti. Ricorrenti sempre di Equal, mai nelle provvigioni agenti. {NOT_COLLECTED}
        </div>
      </CardContent>
    </Card>
  )
}
